import React, { useCallback, useEffect, useState } from 'react';

import { adminApi } from '../../../config/axios';
import { dateFormat, dateTimeFormat } from '../../../config/utils';
import Badge from '../../../components/ui/Badge';
import Spinner from '../../../components/ui/Spinner';
import { Select } from '../../../components/ui/Input';

const SECTIONS = [
  { value: 'ALL',   label: 'All logs' },
  { value: 'LOG',   label: 'Info only' },
  { value: 'ERROR', label: 'Errors only' },
];

export default function DeviceLogs({ deviceId }) {
  const [section, setSection]   = useState('ALL');
  const [page, setPage]         = useState(1);
  const [perPage]               = useState(10);
  const [rows, setRows]         = useState([]);
  const [pagination, setPagination] = useState({ total_pages: 1, total: 0, current_page: 1 });
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);

  const fetchLogs = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.get(`/device/${deviceId}/logs`, {
        params: { section, page, per_page: perPage },
      });
      setRows(res.data?.data ?? []);
      setPagination(res.data?.pagination ?? { total_pages: 1, total: 0, current_page: page });
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Failed to load device logs.');
    } finally {
      setLoading(false);
    }
  }, [deviceId, section, page, perPage]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const badgeVariant = (level) => {
    const l = (level || 'LOG').toUpperCase();
    if (l === 'CRITICAL' || l === 'ERROR') return 'danger';
    if (l === 'WARNING') return 'accent';
    return 'teal';
  };

  return (
    <div className="card-padded">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="section-title">📜 Device Logs</h3>
          <p className="text-text-muted text-xs mt-0.5">
            {pagination.total} total · page {pagination.current_page} of {pagination.total_pages || 1}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={section}
            onChange={e => { setSection(e.target.value); setPage(1); }}
            className="max-w-[180px]"
          >
            {SECTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
          <button
            onClick={fetchLogs}
            className="btn-secondary text-xs"
            disabled={loading}
            title="Refresh"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-8"><Spinner size="md" /></div>
      ) : error ? (
        <p className="text-sm text-danger py-4">{error}</p>
      ) : rows.length === 0 ? (
        <p className="text-text-muted text-sm py-6 text-center">No logs yet for this device.</p>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-text-muted text-xs uppercase tracking-wider">
                <th className="px-3 py-2 w-24">Level</th>
                <th className="px-3 py-2">Message</th>
                <th className="px-3 py-2 w-40">When</th>
                <th className="px-3 py-2 w-24">App</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const message = r.log_message || '—';
                const when    = r.logged_at || r.created_at;
                return (
                  <tr key={r.id ?? i} className="border-t border-white/[0.05]">
                    <td className="px-3 py-2 align-top">
                      <Badge variant={badgeVariant(r.log_level)}>
                        {(r.log_level || 'LOG').toUpperCase()}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 align-top text-text-main break-words">
                      <span className="line-clamp-2">{message}</span>
                    </td>
                    <td className="px-3 py-2 align-top text-text-muted text-xs">{dateTimeFormat(when)}</td>
                    <td className="px-3 py-2 align-top text-text-muted text-xs">{r.app_version || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            className="btn-secondary text-xs"
            disabled={page <= 1 || loading}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            ← Prev
          </button>
          <span className="text-text-muted text-xs">{page} / {pagination.total_pages}</span>
          <button
            className="btn-secondary text-xs"
            disabled={page >= pagination.total_pages || loading}
            onClick={() => setPage(p => p + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
